import { useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Recurso, Zona } from '../../api/ops/pagos';
import type { CambioZona, TurnoZona } from '../../api/ops/reservasGestion';
import type { Union } from '../../api/ops/sala';
import { ErrorApi } from '../../api/problema';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError } from '../../logica/estados';
import { mayuscula } from '../reservas/textos';
import { ASIGNACION, DIAS_SEMANA, MODALIDAD, TIPO_RECURSO, lineaDeZona } from './textos';

/**
 * Paneles de 8.5: zona (datos, «Cómo llamas a lo que se reserva» `unit` G658, cómo se reserva, turnos con
 * `replaceZoneShifts`, asignación G404, «El cliente puede elegir» G405, cobro con `policy_name` G797 y el horario enseñado
 * y enlazado a Horario, G245), recurso (tipo, zona, capacidad y si se junta, G36) y unión predefinida. Archivar con
 * reservas futuras pide confirmarlo (`409 commitments_pending` → `confirm_commitments`). Sin `gestionar`, solo lectura.
 */
type Cerrar = (cambio?: string) => void;
const n = (texto: string, defecto: number) => (/^\d+$/.test(texto.trim()) ? Number(texto.trim()) : defecto);

function mensajeError(e: unknown, defecto: string): string {
    return avisoBreveDeError(e) ?? (e instanceof ErrorApi ? (e.errores[0]?.message ?? e.message) : defecto);
}

/** Archivar con la ventana de compromisos (reservas futuras). */
function useArchivar(archivar: (confirmar: boolean) => Promise<unknown>, onCerrar: Cerrar, hecho: string) {
    const [paso, setPaso] = useState<'no' | 'preguntar' | 'compromisos'>('no');
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ir = (confirmar: boolean) => {
        setOcupado(true);
        archivar(confirmar)
            .then(() => { onCerrar(hecho); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'commitments_pending' && !confirmar) {
                    setPaso('compromisos');
                } else {
                    setError(mensajeError(e, 'No se ha podido archivar.'));
                    setPaso('no');
                }
            })
            .finally(() => { setOcupado(false); });
    };
    const ventana = (
        <>
            <Ventana abierta={paso === 'preguntar'} titulo="¿Archivar?" texto="Deja de verse y de reservarse. Lo ya reservado se mantiene." accion="Archivar" accionIcono="archive" ocupado={ocupado} onAccion={() => { ir(false); }} onCerrar={() => { setPaso('no'); }} />
            <Ventana abierta={paso === 'compromisos'} titulo="Tiene reservas futuras" texto="Si lo archivas, esas reservas quedan para recolocar. Avisa tú a los clientes si hace falta." accion="Archivar igualmente" accionIcono="archive" ocupado={ocupado} onAccion={() => { ir(true); }} onCerrar={() => { setPaso('no'); }} />
        </>
    );
    return { preguntar: () => { setPaso('preguntar'); }, ventana, error };
}

interface PropsZona {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    zona: Zona | null;
    recursos: readonly Recurso[];
    palabra: string;
    gestiona: boolean;
    puedeHorario: boolean;
    onHorario: () => void;
    onCerrar: Cerrar;
}

export function PanelZona({ ops, negocio, movil, zona, recursos, palabra, gestiona, puedeHorario, onHorario, onCerrar }: PropsZona) {
    const [nombre, setNombre] = useState(zona?.name ?? '');
    const [unidad, setUnidad] = useState(zona?.unit ?? '');
    const [modalidad, setModalidad] = useState<'slot' | 'shift'>(zona?.modality === 'shift' ? 'shift' : 'slot');
    const [duracion, setDuracion] = useState(String(zona?.default_duration_minutes ?? 120));
    const [minimo, setMinimo] = useState(String(zona?.min_party ?? 1));
    const [maximo, setMaximo] = useState(String(zona?.max_party ?? 10));
    const [asignacion, setAsignacion] = useState<'at_sale' | 'on_floor'>(zona?.assignment_mode ?? 'on_floor');
    const [elige, setElige] = useState(zona?.customer_picks_resource ?? false);
    const [publica, setPublica] = useState(zona?.public ?? true);
    const [turnos, setTurnos] = useState<TurnoZona[]>(zona?.shifts ?? []);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const suyos = zona === null ? [] : recursos.filter((r) => r.current_terms.zone_id === zona.id);
    const archivo = useArchivar((c) => ops.archiveZone(negocio, zona?.id ?? '', c), onCerrar, 'Zona archivada.');
    const estancia = zona?.modality === 'stay';
    const bloqueado = !gestiona;
    const turnosCambian = JSON.stringify(turnos) !== JSON.stringify(zona?.shifts ?? []);

    const guardar = () => {
        if (nombre.trim() === '') {
            setError('Ponle un nombre.');
            return;
        }
        const campos: CambioZona = {
            name: nombre.trim(),
            unit: unidad.trim() === '' ? null : unidad.trim(),
            min_party: n(minimo, 1),
            max_party: n(maximo, 10),
            assignment_mode: asignacion,
            customer_picks_resource: elige,
            public: publica,
            ...(estancia ? {} : { default_duration_minutes: n(duracion, 120) }),
        };
        setOcupado(true);
        setError(null);
        const hecho = zona === null
            ? ops.createZone(negocio, { ...campos, name: nombre.trim(), modality: modalidad, min_party: n(minimo, 1), max_party: n(maximo, 10), price_mode: 'free', assignment_mode: asignacion, ...(modalidad === 'shift' ? { shifts: turnos } : {}) })
            : ops.updateZone(negocio, zona, campos).then((z) => (modalidad === 'shift' && turnosCambian ? ops.replaceZoneShifts(negocio, z, turnos) : z));
        hecho
            .then(() => { onCerrar(zona === null ? 'Zona creada.' : 'Cambios guardados.'); })
            .catch((e: unknown) => { setError(mensajeError(e, 'No se han podido guardar los cambios.')); })
            .finally(() => { setOcupado(false); });
    };

    const turno = (i: number, cambio: Partial<TurnoZona>) => { setTurnos((t) => t.map((x, j) => (j === i ? { ...x, ...cambio } : x))); };

    return (
        <PanelLateral
            abierto
            onCerrar={() => { onCerrar(); }}
            movil={movil}
            tamano="ficha"
            icono="layers"
            eyebrow="Zona"
            titulo={zona === null ? 'Nueva zona' : (zona.name ?? '')}
            subtitulo={zona === null ? 'Lo que se reserva: un salón, una terraza…' : `${lineaDeZona(zona)} • ${String(suyos.length)} ${palabra}s`}
            {...(gestiona ? { accion: zona === null ? 'Crear' : 'Guardar', ocupado, onAccion: guardar } : {})}
        >
            <div className="rv-sl-form">
                <Campo etiqueta="Nombre" valor={nombre} onChange={setNombre} max={80} bloqueado={bloqueado} />
                <Campo etiqueta="Cómo llamas a lo que se reserva" valor={unidad} onChange={setUnidad} max={40} bloqueado={bloqueado} ayuda={`Mesa, pista, habitación… Si lo dejas vacío, «${palabra}».`} />
                <p className="rv-sl-ceja">CÓMO SE RESERVA</p>
                {estancia ? (
                    <p className="rv-sl-nota">Estancia: se reserva por noches. Se gestiona en Estancias.</p>
                ) : (
                    <>
                        {zona === null ? (
                            <Segmentos etiqueta="Se reserva por" items={[{ etiqueta: 'Franja', icono: 'clock' }, { etiqueta: 'Turnos', icono: 'calendar-clock' }]} valor={modalidad === 'slot' ? 0 : 1} onCambio={(i) => { setModalidad(i === 0 ? 'slot' : 'shift'); }} />
                        ) : (
                            <p className="rv-sl-nota">{`Se reserva por ${(MODALIDAD[modalidad] ?? '').toLocaleLowerCase('es')}.`}</p>
                        )}
                        <Campo etiqueta="Duración (minutos)" modo="numeric" valor={duracion} onChange={setDuracion} bloqueado={bloqueado} />
                    </>
                )}
                <div className="rv-rs-nueva__par">
                    <Campo etiqueta="Personas, como mínimo" modo="numeric" valor={minimo} onChange={setMinimo} bloqueado={bloqueado} />
                    <Campo etiqueta="Personas, como máximo" modo="numeric" valor={maximo} onChange={setMaximo} bloqueado={bloqueado} />
                </div>
                {modalidad === 'shift' && !estancia && (
                    <>
                        <p className="rv-sl-ceja">TURNOS</p>
                        {turnos.length === 0 && <p className="rv-sl-nota">Sin turnos todavía.</p>}
                        {turnos.map((t, i) => (
                            <div key={t.id ?? `nuevo${String(i)}`} className="rv-rc-turno" role="group" aria-label={`Turno ${t.name === '' ? String(i + 1) : t.name}`}>
                                <Campo etiqueta="Nombre del turno" valor={t.name} onChange={(v) => { turno(i, { name: v }); }} max={40} bloqueado={bloqueado} />
                                <Campo etiqueta="Empieza (HH:MM)" valor={t.start_local} onChange={(v) => { turno(i, { start_local: v }); }} max={5} bloqueado={bloqueado} />
                                <Campo etiqueta="Dura (minutos)" modo="numeric" valor={String(t.duration_minutes)} onChange={(v) => { turno(i, { duration_minutes: n(v, t.duration_minutes) }); }} bloqueado={bloqueado} />
                                <div className="rv-rc-dias" role="group" aria-label="Días">
                                    {DIAS_SEMANA.map((d, j) => {
                                        const on = t.weekdays.includes(j + 1);
                                        return (
                                            <button key={d} type="button" aria-pressed={on} disabled={bloqueado} onClick={() => { turno(i, { weekdays: on ? t.weekdays.filter((x) => x !== j + 1) : [...t.weekdays, j + 1].sort() }); }}>{d}</button>
                                        );
                                    })}
                                </div>
                                {gestiona && <button type="button" className="rv-b rv-b-fan" onClick={() => { setTurnos((x) => x.filter((_, j) => j !== i)); }}><Icono nombre="trash-2" /><span>Quitar</span></button>}
                            </div>
                        ))}
                        {gestiona && <button type="button" className="rv-b rv-b-sec" onClick={() => { setTurnos((x) => [...x, { name: '', start_local: '20:00', duration_minutes: 180, weekdays: [1, 2, 3, 4, 5, 6, 7] }]); }}><Icono nombre="plus" /><span>Añadir un turno</span></button>}
                    </>
                )}
                <p className="rv-sl-ceja">ASIGNACIÓN</p>
                <Segmentos etiqueta={`Cuándo se elige el ${palabra}`} items={[{ etiqueta: ASIGNACION.at_sale ?? '', icono: 'calendar-check' }, { etiqueta: ASIGNACION.on_floor ?? '', icono: 'armchair' }]} valor={asignacion === 'at_sale' ? 0 : 1} onCambio={(i) => { if (gestiona) { setAsignacion(i === 0 ? 'at_sale' : 'on_floor'); } }} />
                <Interruptor etiqueta="El cliente puede elegir" ayuda={`En la web elige un ${palabra} concreto. Si no, se lo asignamos nosotros.`} valor={elige} onChange={setElige} bloqueado={bloqueado} />
                <Interruptor etiqueta="Se reserva en la web" ayuda="Web, widget y asistente. Si lo quitas, solo desde el panel." valor={publica} onChange={setPublica} bloqueado={bloqueado} />
                <p className="rv-sl-ceja">PRECIO Y COBRO</p>
                <p className="rv-sl-nota">{`Cobro: ${zona?.policy_name ?? 'sin cobro'}. La política se elige en Pagos.`}</p>
                {zona !== null && (
                    <>
                        <p className="rv-sl-ceja">HORARIO</p>
                        <p className="rv-sl-nota">El horario de la zona es el del negocio salvo que tenga uno propio. Se ve y se cambia en Horario.</p>
                        {puedeHorario && <button type="button" className="rv-b rv-b-sec" onClick={onHorario}><Icono nombre="clock" /><span>Ver el horario</span></button>}
                    </>
                )}
                {zona !== null && gestiona && <button type="button" className="rv-b rv-b-des" onClick={archivo.preguntar}><Icono nombre="archive" /><span>Archivar la zona</span></button>}
                {(error ?? archivo.error) !== null && <p className="rv-sl-resultado rv-sl-resultado--mal" role="alert"><Icono nombre="triangle-alert" /><span>{error ?? archivo.error}</span></p>}
            </div>
            {archivo.ventana}
        </PanelLateral>
    );
}

interface PropsRecurso {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    recurso: Recurso | null;
    zonas: readonly Zona[];
    palabra: string;
    gestiona: boolean;
    onCerrar: Cerrar;
}

export function PanelRecurso({ ops, negocio, movil, recurso, zonas, palabra, gestiona, onCerrar }: PropsRecurso) {
    const [codigo, setCodigo] = useState(recurso?.code ?? '');
    const [tipo, setTipo] = useState<Recurso['kind']>(recurso?.kind ?? 'table');
    const [zona, setZona] = useState(recurso?.current_terms.zone_id ?? zonas[0]?.id ?? '');
    const [minimo, setMinimo] = useState(String(recurso?.current_terms.min_capacity ?? 1));
    const [maximo, setMaximo] = useState(String(recurso?.current_terms.max_capacity ?? 4));
    const [junta, setJunta] = useState(recurso?.current_terms.joinable ?? false);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const archivo = useArchivar((c) => ops.archiveResource(negocio, recurso?.id ?? '', c), onCerrar, 'Archivado.');
    const bloqueado = !gestiona;
    const guardar = () => {
        if (codigo.trim() === '') {
            setError('Ponle un código (por ejemplo, T4).');
            return;
        }
        const terminos = { zone_id: zona, min_capacity: n(minimo, 1), max_capacity: n(maximo, 4), joinable: junta };
        setOcupado(true);
        setError(null);
        (recurso === null
            ? ops.createResource(negocio, { code: codigo.trim(), kind: tipo, terms: terminos })
            : ops.updateResource(negocio, recurso, { code: codigo.trim(), kind: tipo, terms: terminos }))
            .then(() => { onCerrar(recurso === null ? 'Creado.' : 'Cambios guardados.'); })
            .catch((e: unknown) => { setError(mensajeError(e, 'No se han podido guardar los cambios.')); })
            .finally(() => { setOcupado(false); });
    };
    return (
        <PanelLateral
            abierto
            onCerrar={() => { onCerrar(); }}
            movil={movil}
            icono="layout-grid"
            eyebrow={mayuscula(palabra)}
            titulo={recurso === null ? `Nuevo ${palabra}` : `${mayuscula(palabra)} ${recurso.code ?? ''}`}
            {...(gestiona ? { accion: recurso === null ? 'Crear' : 'Guardar', ocupado, onAccion: guardar } : {})}
        >
            <div className="rv-sl-form">
                <Campo etiqueta="Código" valor={codigo} onChange={setCodigo} max={20} bloqueado={bloqueado} ayuda="Lo que se ve en el plano y en las reservas: T4, S2, Hab. 3…" />
                <Desplegable etiqueta="Tipo" valor={tipo} opciones={Object.entries(TIPO_RECURSO).map(([valor, texto]) => ({ valor, texto }))} onChange={(v) => { setTipo(v as Recurso['kind']); }} bloqueado={bloqueado} />
                <Desplegable etiqueta="Zona" valor={zona} opciones={zonas.map((z) => ({ valor: z.id, texto: z.name ?? '' }))} onChange={setZona} bloqueado={bloqueado} />
                <div className="rv-rs-nueva__par">
                    <Campo etiqueta="Personas, como mínimo" modo="numeric" valor={minimo} onChange={setMinimo} bloqueado={bloqueado} />
                    <Campo etiqueta="Personas, como máximo" modo="numeric" valor={maximo} onChange={setMaximo} bloqueado={bloqueado} />
                </div>
                <Interruptor etiqueta="Se puede juntar" ayuda="Con otros de la misma zona, en una unión o en Sala." valor={junta} onChange={setJunta} bloqueado={bloqueado} />
                {recurso !== null && gestiona && <button type="button" className="rv-b rv-b-des" onClick={archivo.preguntar}><Icono nombre="archive" /><span>Archivar</span></button>}
                {(error ?? archivo.error) !== null && <p className="rv-sl-resultado rv-sl-resultado--mal" role="alert"><Icono nombre="triangle-alert" /><span>{error ?? archivo.error}</span></p>}
            </div>
            {archivo.ventana}
        </PanelLateral>
    );
}

interface PropsUnion {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    union: Union | null;
    zonas: readonly Zona[];
    recursos: readonly Recurso[];
    gestiona: boolean;
    onCerrar: Cerrar;
}

export function PanelUnion({ ops, negocio, movil, union, zonas, recursos, gestiona, onCerrar }: PropsUnion) {
    const [nombre, setNombre] = useState(union?.name ?? '');
    const [zona, setZona] = useState(union?.zone_id ?? zonas[0]?.id ?? '');
    const [elegidos, setElegidos] = useState<string[]>(union?.resource_ids ?? []);
    const [minimo, setMinimo] = useState(String(union?.min_capacity ?? 2));
    const [maximo, setMaximo] = useState(String(union?.max_capacity ?? 8));
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const archivo = useArchivar(() => ops.archiveResourceUnion(negocio, union?.id ?? ''), onCerrar, 'Unión archivada.');
    const unibles = recursos.filter((r) => r.current_terms.zone_id === zona && r.current_terms.joinable);
    const guardar = () => {
        if (elegidos.length < 2) {
            setError('Elige al menos dos para juntar.');
            return;
        }
        setOcupado(true);
        setError(null);
        const campos = { name: nombre.trim(), resource_ids: elegidos, min_capacity: n(minimo, 2), max_capacity: n(maximo, 8) };
        (union === null ? ops.createResourceUnion(negocio, { ...campos, zone_id: zona }) : ops.updateResourceUnion(negocio, union, campos))
            .then(() => { onCerrar(union === null ? 'Unión creada.' : 'Cambios guardados.'); })
            .catch((e: unknown) => { setError(mensajeError(e, 'No se han podido guardar los cambios.')); })
            .finally(() => { setOcupado(false); });
    };
    return (
        <PanelLateral
            abierto
            onCerrar={() => { onCerrar(); }}
            movil={movil}
            icono="link"
            eyebrow="Unión"
            titulo={union === null ? 'Nueva unión' : (union.name ?? 'Unión')}
            subtitulo="Varios que se reservan juntos para grupos grandes."
            {...(gestiona ? { accion: union === null ? 'Crear' : 'Guardar', ocupado, onAccion: guardar } : {})}
        >
            <div className="rv-sl-form">
                <Campo etiqueta="Nombre" valor={nombre} onChange={setNombre} max={40} bloqueado={!gestiona} />
                <Desplegable etiqueta="Zona" valor={zona} opciones={zonas.map((z) => ({ valor: z.id, texto: z.name ?? '' }))} onChange={(v) => { setZona(v); setElegidos([]); }} bloqueado={!gestiona || union !== null} />
                <div className="rv-sl-libres" role="group" aria-label="Qué se junta">
                    {unibles.length === 0 && <p className="rv-sl-nota">No hay ninguno que se pueda juntar en esta zona.</p>}
                    {unibles.map((r) => (
                        <button key={r.id} type="button" aria-pressed={elegidos.includes(r.id)} disabled={!gestiona} className={`rv-sl-libre${elegidos.includes(r.id) ? ' rv-sl-libre--elegido' : ''}`} onClick={() => { setElegidos((x) => (x.includes(r.id) ? x.filter((y) => y !== r.id) : [...x, r.id])); }}>
                            <strong>{r.code}</strong>
                            <small>{`${String(r.current_terms.min_capacity)}–${String(r.current_terms.max_capacity)}`}</small>
                        </button>
                    ))}
                </div>
                <div className="rv-rs-nueva__par">
                    <Campo etiqueta="Personas, como mínimo" modo="numeric" valor={minimo} onChange={setMinimo} bloqueado={!gestiona} />
                    <Campo etiqueta="Personas, como máximo" modo="numeric" valor={maximo} onChange={setMaximo} bloqueado={!gestiona} />
                </div>
                {union !== null && gestiona && <button type="button" className="rv-b rv-b-des" onClick={archivo.preguntar}><Icono nombre="archive" /><span>Archivar la unión</span></button>}
                {(error ?? archivo.error) !== null && <p className="rv-sl-resultado rv-sl-resultado--mal" role="alert"><Icono nombre="triangle-alert" /><span>{error ?? archivo.error}</span></p>}
            </div>
            {archivo.ventana}
        </PanelLateral>
    );
}
